import type { ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Drawer } from "vaul";
import { useIsMobile } from "./ui/use-mobile";

interface ResponsiveSheetProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name announced by screen readers (rendered visually hidden). */
  label: string;
  /** Desktop presentation: right slide-over panel or centered dialog. */
  desktop?: "right" | "center";
  /** Width class for the desktop panel, e.g. "max-w-[520px]". */
  desktopWidth?: string;
  /** Mobile sheet height: "tall" (~92dvh, card detail) or "auto" (pickers). */
  mobileHeight?: "tall" | "auto";
  /** Base z-index for the overlay; content renders one above. */
  zIndex?: number;
  children: ReactNode;
}

/**
 * One wrapper for every modal/slide-over in the app: a vaul bottom sheet with
 * drag-handle + swipe-to-close on mobile (<768px), and the pre-existing
 * desktop presentation (right slide-over or centered dialog) at ≥768px.
 */
export function ResponsiveSheet({
  open,
  onClose,
  label,
  desktop = "center",
  desktopWidth = desktop === "right" ? "max-w-[520px]" : "max-w-[440px]",
  mobileHeight = "auto",
  zIndex = 60,
  children,
}: ResponsiveSheetProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }} repositionInputs={false}>
        <Drawer.Portal>
          <Drawer.Overlay
            className="fixed inset-0 bg-foreground/20 backdrop-blur-sm"
            style={{ zIndex }}
          />
          <Drawer.Content
            className="fixed inset-x-0 bottom-0 flex flex-col rounded-t-3xl bg-card border-t border-border/40 shadow-2xl shadow-foreground/10 outline-none"
            style={{
              zIndex: zIndex + 1,
              maxHeight: "92dvh",
              height: mobileHeight === "tall" ? "92dvh" : undefined,
            }}
          >
            <Drawer.Title className="sr-only">{label}</Drawer.Title>
            <Drawer.Description className="sr-only">{label}</Drawer.Description>
            <div className="shrink-0 flex justify-center pt-3 pb-1" aria-hidden="true">
              <div className="h-1.5 w-12 rounded-full bg-muted-foreground/25" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pb-safe">
              {children}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-foreground/10 backdrop-blur-sm"
            style={{ zIndex }}
            onClick={onClose}
          />
          {desktop === "right" ? (
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              role="dialog"
              aria-label={label}
              className={`fixed top-0 right-0 h-full w-full ${desktopWidth} bg-card border-l border-border/40 shadow-2xl shadow-foreground/5 overflow-y-auto`}
              style={{ zIndex: zIndex + 1 }}
            >
              {children}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              role="dialog"
              aria-label={label}
              className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full ${desktopWidth} flex flex-col max-h-[90dvh] bg-card rounded-2xl border border-border/40 shadow-2xl shadow-foreground/10 overflow-hidden`}
              style={{ zIndex: zIndex + 1 }}
            >
              {/* Cap at the viewport and scroll inside, so tall content (e.g. the
                  cover cropper expanding) never grows off-screen unreachably. */}
              <div className="min-h-0 overflow-y-auto overscroll-contain">
                {children}
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
